import { get, post, put } from '@/lib/api/client'
import { setAuthToken, clearAuthToken, getAuthToken } from '@/lib/api/client'
import type {
  LoginRequest,
  LoginResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  AdminUser,
  ApiResponse,
  UserProfileResponse,
  CreateAdminUserRequest,
  VerifyAccountInviteRequest,
  ActivateAccountRequest,
  ResetPasswordRequest
} from '@/lib/api/types'

// Store current user and pending state
let currentUser: AdminUser | null = null
let pendingUserId: string | null = null

// Create admin user with all available details
function createAdminUser(profile: UserProfileResponse, fallbackEmail?: string): AdminUser {
  return {
    adminId: profile.userId, // Using userId as adminId for now
    userProfile: {
      userId: profile.userId,
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      emailAddress: profile.emailAddress || fallbackEmail || ''
    },
    isDefaultPassword: profile.isDefaultPassword || false,
    userRole: profile.userRole || '',
    otpChannel: profile.otpChannel,
    hasActivatedAccount: profile.hasActivatedAccount || false,
    isTwoFactorEnabled: profile.isTwoFactorEnabled || false
  }
}

// Login function
export async function login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse> & { requires2FA: boolean }> {
  const response = await post<ApiResponse<LoginResponse>>('admins/public/authenticate', credentials)

  if (response.success && response.data) {
    const { token, userProfileResponse } = response.data

    if (token) {
      // 2FA is enabled - user is fully authenticated
      setAuthToken(token)

      const adminUser = createAdminUser(userProfileResponse, credentials.emailAddress)
      currentUser = adminUser
      sessionStorage.setItem('current_user', JSON.stringify(adminUser))

      return { ...response, requires2FA: false }
    } else {
      // 2FA is disabled - need OTP verification
      pendingUserId = userProfileResponse.userId
      sessionStorage.setItem('pending_user_id', userProfileResponse.userId)

      return { ...response, requires2FA: true }
    }
  }

  return { ...response, requires2FA: false }
}

// Verify OTP function
export async function verifyOtp(credentials: VerifyOtpRequest): Promise<ApiResponse<VerifyOtpResponse>> {
  const response = await post<ApiResponse<VerifyOtpResponse>>('admins/public/verify-login-otp', credentials)

  if (response.success && response.data) {
    const { token, userProfileResponse } = response.data

    // Ensure we have both token and userProfileResponse before proceeding
    if (token && userProfileResponse) {
      setAuthToken(token)

      const adminUser = createAdminUser(userProfileResponse, userProfileResponse.emailAddress)
      currentUser = adminUser
      sessionStorage.setItem('current_user', JSON.stringify(adminUser))

      // Clear pending state
      pendingUserId = null
      sessionStorage.removeItem('pending_user_id')
    }
  }

  return response
}

// Logout function
export async function logout(): Promise<void> {
  try {
    // Call logout endpoint if needed
    // await post('/authenticate/logout')
  } catch (error) {
    console.warn('Logout API call failed:', error)
  } finally {
    // Clear all auth state
    currentUser = null
    pendingUserId = null
    clearAuthToken()
    sessionStorage.removeItem('current_user')
    sessionStorage.removeItem('pending_user_id')
  }
}

// Get current user
export function getCurrentUser(): AdminUser | null {
  if (!currentUser) {
    const stored = sessionStorage.getItem('current_user')
    if (stored) {
      try {
        currentUser = JSON.parse(stored)
      } catch {
        sessionStorage.removeItem('current_user')
      }
    }
  }
  return currentUser
}

// Update current user (used when profile is updated)
export function updateCurrentUser(updatedUser: AdminUser): void {
  // Update sessionStorage first
  sessionStorage.setItem('current_user', JSON.stringify(updatedUser))
  // Update module-level cache
  currentUser = updatedUser
}

// Clear current user cache (forces next getCurrentUser() to read from sessionStorage)
export function clearCurrentUserCache(): void {
  currentUser = null
}

// Check if authenticated
export function isAuthenticated(): boolean {
  const token = getAuthToken()
  const user = getCurrentUser()
  return !!(token && user)
}

// Get pending user ID for OTP
export function getPendingUserId(): string | null {
  if (!pendingUserId) {
    pendingUserId = sessionStorage.getItem('pending_user_id')
  }
  return pendingUserId
}

// Check user role
export function hasRole(role: string): boolean {
  const user = getCurrentUser()
  return user?.userRole === role
}

// Initialize auth on app start
export function initializeAuth(): void {
  const token = getAuthToken()
  if (token) {
    getCurrentUser() // This will restore from sessionStorage
  }
}

// Invite/Create admin user
export async function inviteAdminUser(data: CreateAdminUserRequest): Promise<ApiResponse<AdminUser>> {
  return await post<ApiResponse<AdminUser>>('admins/private/create', data)
}

// Verify account invite (for account activation)
export async function verifyAccountInvite(data: VerifyAccountInviteRequest): Promise<ApiResponse<null>> {
  return await post<ApiResponse<null>>('admins/public/verify-account-invite', data)
}

// Activate account (set password)
export async function activateAccount(data: ActivateAccountRequest): Promise<ApiResponse<null>> {
  return await post<ApiResponse<null>>('admins/public/activate-account', data)
}

// Forgot password (send OTP to email)
export async function forgotPassword(emailAddress: string): Promise<ApiResponse<null>> {
  const endpoint = `admins/public/forgot-password?emailAddress=${encodeURIComponent(emailAddress)}`
  return await get<ApiResponse<null>>(endpoint)
}

// Reset password (for password reset flow)
export async function resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<null>> {
  return await put<ApiResponse<null>>('admins/public/reset-password', data)
}